import React, { useCallback, useEffect, useState } from 'react';
import AdminTemplate from '../../layout/AdminTemplate';
import requireAuth from '../../components/hocs/requireAuth';
import { Button, Card, Col, Flex, Layout, message, Row, Typography } from 'antd';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { SeoReportUrlsTable } from './SeoReportUrlsTable';
import { useExportSeoReportCSVMutation, useGetSeoReportQuery, useGetSeoReportUrlsQuery } from './seoReportApiSlice';
import { ReportDetailsCard } from './components/ReportDetailsCard';
import { SeoReportChart } from './components/SeoReportChart';
import { debounce } from 'lodash';
import { LeftOutlined } from '@ant-design/icons';
import useFeatureFlag, { FeatureFlag } from '../../hooks/useFeatureFlag';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import ExportCSVModal from './components/ExportCSVModal';
import { HiddenContent } from '../../components/BlurringOverlay';
import { useLazyGetDomainStatisticsQuery } from '../domain-manager/api/domainManagerApiSlice';

const META_TAGS = {
  title: 'SEO Report - Prerender.io',
};
const { Title } = Typography;
const { Content } = Layout;

const PAGE_SIZE = 50;

const getResponseMessage = (response, email) => {
  switch (response?.status) {
    case 'queued':
      return {
        title: 'Export SEO Report scheduled',
        content: email
          ? <Typography.Paragraph>We will email a download link to <strong>{email}</strong> when your export is ready.</Typography.Paragraph>
          : <Typography.Paragraph>We have scheduled your export. You will get a download link in your email once the export is ready.</Typography.Paragraph>
      };
    case 'not queued':
      return {
        title: 'Export CSV',
        content:
          // eslint-disable-next-line max-len
          <Typography.Paragraph>Click start to export all your cached URLs into a CSV. Please note that exporting URLs happens in the background and you'll be notified by email once it's done.</Typography.Paragraph>
      };
    case 'processing':
      return {
        title: 'Export CSV',
        content: <Typography.Paragraph>You already have an export job in progress. We'll inform you via email if that job has finished.</Typography.Paragraph>
      };
    case 'already exists':
      return {
        title: 'Export CSV',
        content: <Typography.Paragraph>CSV export has been already requested. Please try again later.</Typography.Paragraph>
      };
    case 'limited':
      return {
        title: 'Export CSV',
        content: <Typography.Paragraph>Please upgrade your plan to use this feature.</Typography.Paragraph>
      };
    default:
      return {
        title: 'Export CSV',
        content: <Typography.Paragraph>Something bad has happened. We're looking into it...</Typography.Paragraph>
      };
  }
};

const mapToApiPayload = (params) => {
  return {
    domain: params.domain,
    domainId: params.domainId,
    page: params.pagination.current,
    pageSize: params.pagination.pageSize,
    sort: params.sorter?.field,
    sortDirection: params.sorter?.order === 'ascend' ? 'ASC' : 'DESC',
    q: params.filters?.url ? params.filters?.url[0] : undefined,
    adaptive_type: params.filters?.adaptive_type ? params.filters?.adaptive_type[0] : undefined,
  };
};

function SeoExportCSVModal({ visible, onClose, seoReportSummary, tableParams, exportCsv, user }) {
  const [exportTriggered, setExportTriggered] = useState(false);
  const [title, setTitle] = useState('Export CSV');
  const [modalText, setModalText] = useState(
    <Typography.Paragraph>Export SEO Report for this domain as CSV?</Typography.Paragraph>
  );

  return (
    <ExportCSVModal
      exportTriggered={exportTriggered}
      modalTitle={title}
      onClose={() => {
        onClose();
        setTitle('Export CSV');
        setModalText(<Typography.Paragraph>Export SEO Report for this domain as CSV?</Typography.Paragraph>);
        setExportTriggered(false);
      }}
      onExport={() => {
        const exportParams = {
          domain: seoReportSummary?.domain,
          sort: tableParams.sorter?.field,
          sortDirection: tableParams.sorter?.order === 'descend' ? 'DESC' : 'ASC',
          q: tableParams.filters?.url ? tableParams.filters?.url[0] : undefined,
          adaptive_type: tableParams.filters?.adaptive_type ? tableParams.filters?.adaptive_type[0] : undefined,
        };

        exportCsv(exportParams)
          .unwrap()
          .then((result) => {
            const msg = getResponseMessage(result, user.email);
            setTitle(msg.title);
            setModalText(msg.content);
          })
          .catch(() => {
            setTitle('Export CSV');
            setModalText(<Typography.Paragraph>Failed to export CSV</Typography.Paragraph>);
          })
          .finally(() => {
            setExportTriggered(true);
          });
      }}
      showModal={visible}
      text={modalText}
    />
  );
}

const SeoReportPage = ({ areWeTesting = false }) => {
  const { id: domainId } = useParams();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
    sorter: {
      field: 'last_refresh',
      order: 'descend',
    },
  });
  const {
    data: seoReportSummary,
    error: seoReportError,
    isFetching: isFetchingSeoReport,
  } = useGetSeoReportQuery({ domainId });
  const {
    data: seoReportUrls,
    error: seoReportUrlsError,
    isFetching: isFetchingSeoReportUrls,
  } = useGetSeoReportUrlsQuery(
    mapToApiPayload({ ...tableParams, domain: seoReportSummary?.domain, domainId: domainId }),
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [getDomainStatistics, domainStatisticsResult] = useLazyGetDomainStatisticsQuery();
  const [exportCsv, result] = useExportSeoReportCSVMutation();
  const user = usePrerenderUser();
  const [showModal, setShowModal] = useState(false);

  const isSeoReportPageAvailable = useFeatureFlag([FeatureFlag.SeoReport]);

  const domain = location.state?.domain || seoReportSummary?.domain;

  const totalPages = seoReportSummary
    ? seoReportSummary.good + seoReportSummary.needsImprovement + seoReportSummary.poor
    : 0;

  useEffect(() => {
    if (seoReportSummary?.domain) {
      getDomainStatistics({ domain: seoReportSummary.domain });
    }
  }, [seoReportSummary?.domain]);

  useEffect(() => {
    if (seoReportError) {
      messageApi.error({ content: 'Failed to fetch SEO report summary' });
    }
    if (seoReportUrlsError) {
      messageApi.error({ content: 'Failed to fetch URLs' });
    }
    if (domainStatisticsResult.error) {
      messageApi.error({ content: 'Failed to fetch domain statistics' });
    }
  }, [seoReportError, seoReportUrlsError, domainStatisticsResult.error]);

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams((oldParams) => {
      return { ...oldParams, pagination, sorter, filters };
    });
  };

  const debouncedSetTableParams = useCallback(
    debounce((newInput) => {
      setTableParams((oldParams) => ({
        ...oldParams,
        pagination: { ...oldParams.pagination, current: 1 },
        filters: { ...oldParams.filters, url: [newInput] },
      }));
    }, 1500),
    []
  );

  if (!areWeTesting && (!isSeoReportPageAvailable || !domainId || !Number.isFinite(Number(domainId)))) {
    return <Navigate to="/404" />;
  }

  return (
    <AdminTemplate metaTags={META_TAGS}>
      {contextHolder}
      <Content>
        <SeoExportCSVModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          seoReportSummary={seoReportSummary}
          tableParams={tableParams}
          exportCsv={exportCsv}
          user={user}
        />
        <Flex vertical gap={24}>
          {!seoReportSummary?.domain || isFetchingSeoReport ? (
            <HiddenContent showLoading={true} loadingText="Calculating...">
              <Flex gap="large">
                <Title level={3}>Seo Report</Title>
              </Flex>
              <Row gutter={[24, 24]}>
                <Col xs={8} xl={7}>
                  <ReportDetailsCard averageScore={0} totalPages={0} />
                </Col>
                <Col xs={24} xl={17}>
                  <Card>
                    <SeoReportChart data={[]} />
                  </Card>
                </Col>
              </Row>
            </HiddenContent>
          ) : (
            <>
              <Flex gap="large">
                <Link to="/domain-manager">
                  <Button icon={<LeftOutlined />} type="link">
                    Back
                  </Button>
                </Link>
                <Title level={3}>Seo Report for {domain}</Title>
              </Flex>
              <Row gutter={[24, 24]}>
                <Col xs={8} xl={7}>
                  <ReportDetailsCard averageScore={seoReportSummary?.averageScore} totalPages={totalPages} />
                </Col>
                <Col xs={24} xl={17}>
                  <Card>
                    <SeoReportChart data={seoReportSummary} />
                  </Card>
                </Col>
              </Row>
            </>
          )}

          <Card>
            <SeoReportUrlsTable
              urls={seoReportUrls?.urls}
              isFetching={isFetchingSeoReportUrls || domainStatisticsResult.isFetching}
              tableParams={tableParams}
              handleTableChange={handleTableChange}
              debouncedSetTableParams={debouncedSetTableParams}
              totalUrls={domainStatisticsResult.data?.data[0]?.number_of_urls}
              exportModalOpen={() => setShowModal(true)}
            />
          </Card>
        </Flex>
      </Content>
    </AdminTemplate>
  );
};

export default requireAuth(SeoReportPage);
export { SeoReportPage as TestableSeoReportPage };